import { useState } from "react";
import { Bot, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

// "Ligar com IA" (T6 do HANDOFF-vapi): o agente de voz liga para o contato.
//
// A confirmação existe porque a ligação SAI na hora e vale minuto na VAPI — e
// porque o objetivo daquela chamada é o que separa uma ligação útil de um
// agente falando genérico. Ele vira variável `{{objetivo}}` da chamada, não
// prompt do assistant: o assistant é o mesmo para todo mundo.

const OBJETIVO_MAXIMO = 500;

const LigarComIa = ({
	agentes,
	conexoes,
	nome,
	desabilitado,
	size,
	variant,
	className,
	mostrarRotulo,
	icone,
	aoLigar,
}) => {
	const [agente, setAgente] = useState(null);
	const [conexaoId, setConexaoId] = useState("");
	const [objetivo, setObjetivo] = useState("");
	const [ligando, setLigando] = useState(false);

	const abrir = (escolhido) => {
		setAgente(escolhido);
		setConexaoId(String(conexoes[0]?.id || ""));
		setObjetivo("");
	};

	const fechar = () => {
		if (ligando) return;
		setAgente(null);
	};

	const confirmar = async () => {
		setLigando(true);

		try {
			await aoLigar(conexaoId || conexoes[0]?.id, agente.id, objetivo.trim());
			setAgente(null);
		} catch {
			// A mensagem já apareceu (quem chama traduz o erro): o modal fica
			// aberto para tentar de novo ou trocar de conexão.
		}

		setLigando(false);
	};

	const dica = desabilitado
		? "Já há uma chamada em andamento"
		: `Ligar para ${nome} com um agente de voz`;

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						type="button"
						size={size}
						variant={variant}
						className={className}
						disabled={desabilitado}
						aria-label={dica}
						title={dica}
						onClick={(e) => e.stopPropagation()}
					>
						{icone || <Bot className="h-4 w-4" />}
						{mostrarRotulo ? "Ligar com IA" : null}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuLabel>Ligar com o agente</DropdownMenuLabel>
					{agentes.map((a) => (
						<DropdownMenuItem
							key={a.id}
							onClick={(e) => {
								e.stopPropagation();
								abrir(a);
							}}
						>
							{a.name}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>

			<Dialog open={Boolean(agente)} onOpenChange={(v) => !v && fechar()}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Ligar com {agente?.name}</DialogTitle>
						<DialogDescription>
							O agente liga para <strong>{nome}</strong> e conversa sozinho.
							Você pode assumir a chamada a qualquer momento, pela barra da
							chamada.
						</DialogDescription>
					</DialogHeader>

					{conexoes.length > 1 ? (
						<div className="space-y-1.5">
							<Label>Ligar por</Label>
							<Select value={conexaoId} onValueChange={setConexaoId}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{conexoes.map((c) => (
										<SelectItem key={c.id} value={String(c.id)}>
											{c.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					) : null}

					<div className="space-y-1.5">
						<Label htmlFor="objetivo-da-ligacao">
							Objetivo da ligação (opcional)
						</Label>
						<Textarea
							id="objetivo-da-ligacao"
							rows={3}
							maxLength={OBJETIVO_MAXIMO}
							value={objetivo}
							onChange={(e) => setObjetivo(e.target.value)}
							placeholder="Ex.: confirmar a entrega do pedido 1234 para amanhã à tarde."
						/>
						<p className="text-xs text-muted-foreground">
							Vai como contexto só desta chamada. O agente segue as instruções
							dele para o resto.
						</p>
					</div>

					<DialogFooter className="gap-2">
						<Button variant="outline" onClick={fechar} disabled={ligando}>
							Cancelar
						</Button>
						<Button onClick={confirmar} disabled={ligando}>
							{ligando ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Bot className="h-4 w-4" />
							)}
							Ligar agora
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default LigarComIa;
